import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router";
import { Link, useHistory } from "react-router-dom";
import { ReactComponent as ChevronRight } from "../../../../assets/img/icon/chevron_right_black_24dp.svg";
import { ReactComponent as People } from "../../../../assets/img/icon/people_black_24dp.svg";
import { COLOR, GRAPH_COLOR, VARIABLE } from "../../../../config/constant";
import { getPercPrediction } from "../../../../helpers/helper";
import { Patient } from "../../../../models/Patient";
import { getPulmonaryFunc } from "../../../../services/httpService/pulmonaryFuncRequest";
import { getRecordComparison } from "../../../../services/httpService/recordRequest";
import { GlobalContext } from "../../../contexts/globalContext";
import ChartLine from "../../../ui/atoms/ChartLine/ChartLine";
import PageTitle from "../../../ui/atoms/PageTitle/PageTitle";
import PatientInfo from "../../../ui/atoms/PatientInfo/PatientInfo";

const parameters = [
  { label: "FVC", unit: "L", key: "FVC" },
  { label: "FEV1", unit: "L", key: "FEV1" },
  { label: "FEV1/FVC", unit: "%", key: "ratio" },
  { label: "PEFR", unit: "L/s", key: "PEF" },
];

const PatientReportComparison = () => {
  const isMounted = useRef(true);
  const params = useParams();
  const location = useLocation();
  const history = useHistory();
  const [chartData, setChartData] = useState({
    data1: "",
    data2: "",
  });
  const [legends, setLegends] = useState("");
  const [sharedState, dispatch] = useContext(GlobalContext);
  const [patient, setPatient] = useState();
  const [pulmonaryFunc, setPulmonaryFunc] = useState({
    FEV: "",
    FEV1: "",
    ratio: "",
    PEF: "",
  });
  const [records, setRecords] = useState([]);

  const handleGetData = async () => {
    try {
      let { search } = location;
      if (search === "") {
        throw new Error("alamat url tidak valid");
      }
      search = search.replace("?data=", "_ids=");
      let responses = await Promise.all([
        getRecordComparison(search),
        getPulmonaryFunc(params.id),
      ]);

      const profile = new Patient(
        params.id,
        responses[0].result.user.name,
        responses[0].result.user.profile
      );
      setPatient(profile);

      const pulmonaryFunc = responses[1].result;
      let records = responses[0]["result"]["records"];

      records = records.map((record) => {
        let { sensors } = record;
        let pcPred = {};
        parameters.forEach((param) => {
          sensors[param.key] =
            param.key === "ratio"
              ? sensors[param.key] * 100
              : sensors[param.key];
          pcPred[param.key] = getPercPrediction(
            pulmonaryFunc[param.key],
            sensors[param.key]
          );
        });
        record["pcPred"] = pcPred;

        return record;
      });

      let maxVT = 0;
      let vtIdx = 0;

      records.map((record, idx) => {
        if (record.sensors.VT.length > maxVT) {
          maxVT = record.sensors.VT.length;
          vtIdx = idx;
        }
      });

      let chartData = {
        data1: {
          key: records.map((record, idx) => `FC_${idx}`),
          val: records.reduce((prev, curr, idx) => {
            let varName = `FC_${idx}`;
            let data = curr.sensors.VC.map((data, idx) => {
              return {
                VC: data,
                [varName]: curr.sensors.FC[idx],
              };
            });
            return [...prev, ...data];
          }, []),
        },
        data2: {
          key: records.map((record, idx) => `VT_${idx}`),
          val: records[vtIdx].sensors.VT.map((el, idx) => {
            let result = {
              idx: idx / 50,
            };
            records.forEach((el, recidx) => {
              result[`VT_${recidx}`] = el.sensors.VT[idx];
            });
            return result;
          }),
        },
      };

      setChartData(chartData);
      setPulmonaryFunc(pulmonaryFunc);
      setRecords(records);
      setLegends(records.map((record) => `tes-${record.testNumber}`));
    } catch (error) {
      dispatch("alert", {
        message: error.message,
        show: true,
        type: "warning",
      });
      history.push(`/patient/${params.id}`);
    }
  };

  useEffect(() => {
    handleGetData();
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <>
      <div className="mt-2 lg:mt-6">
        <PageTitle
          title="Halaman Pasien"
          description="Dapatkan informasi pasien beserta dengan riwayat medisnya."
          icon={
            <People className="w-10 sm:w-12 lg:w-16 h-10 sm:h-12 lg:h-16 fill-current text-white" />
          }
        />
        <PatientInfo patient={patient} className="mt-6" />

        <div className="mt-10 flex justify-end items-center">
          <Link to={`/patient/${params.id}`}>
            <div className="text-primary cursor-pointer flex items-center ">
              <div>
                <ChevronRight fill={COLOR.PRIMARY} className="w-6 h-6" />
              </div>
              <div className="ml-1 border-b border-transparent hover:border-primary">
                Kembali
              </div>
            </div>
          </Link>
        </div>

        <div className="mt-6 pb-24">
          <div className="w-full">
            <div className="border border-primary shadow-lg rounded-lg px-2 md:px-4 pt-3 pb-5">
              <div>
                <h4 className="font-semibold">
                  Perbandingan Pengukuran Mode Spirometer
                </h4>
                <div className="mt-3 font-semibold">&bull; Informasi Umum</div>

                <table className="mt-1">
                  <tbody>
                    <tr>
                      <td className="w-36">Mode</td>
                      <td className="px-3">:</td>
                      <td className="capitalize">
                        {records.length !== 0 ? records[0].mode : "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className="w-36">Banyak Pengukuran</td>
                      <td className="px-3">:</td>
                      <td>{records.length} Kali</td>
                    </tr>
                  </tbody>
                </table>

                <div className="mt-3 font-semibold">
                  &bull; Hasil Pengukuran
                </div>

                <div className="w-full overflow-x-auto">
                  <table className="mt-1 w-full">
                    <thead>
                      <tr className="text-white">
                        <th
                          className="bg-secondary px-1 py-2 w-48 border border-white"
                          rowSpan="2"
                        >
                          Parameter
                        </th>
                        <th
                          className="bg-secondary px-1 py-2 w-20 border border-white"
                          rowSpan="2"
                        >
                          Satuan
                        </th>
                        <th
                          className="bg-secondary px-1 py-2 w-20 border border-white"
                          rowSpan="2"
                        >
                          Pred
                        </th>
                        {records.map((record, idx) => {
                          const opacity = idx % 2 === 0 ? "bg-opacity-80" : "";
                          return (
                            <th
                              key={"header" + record._id}
                              className={`px-1 py-2 w-20 bg-secondary border-l border-r border-white ${opacity}`}
                              colSpan="2"
                            >
                              {`Tes ke-${record.testNumber}`}
                            </th>
                          );
                        })}
                      </tr>
                      <tr className="text-white">
                        {records.map((record, idx) => {
                          const opacity = idx % 2 === 0 ? "bg-opacity-80" : "";
                          return (
                            <>
                              <th
                                key={"header-val" + idx + record.testNumber}
                                className={`px-1 py-2 w-20 bg-secondary border-l border-b border-white ${opacity}`}
                              >
                                Nilai
                              </th>
                              <th
                                key={"header-pred" + idx + record.testNumber}
                                className={`px-1 py-2 w-20 bg-secondary border-r border-b border-white ${opacity}`}
                              >
                                %Pred
                              </th>
                            </>
                          );
                        })}
                      </tr>
                    </thead>

                    <tbody>
                      {parameters.map((param, idx) => (
                        <tr
                          key={"row" + param.key}
                          className={
                            "bg-secondary " +
                            (idx % 2 === 0 ? "bg-opacity-0" : "bg-opacity-20")
                          }
                        >
                          <td className="px-1 py-1.5">{param.label}</td>
                          <td className="px-1 py-1.5 text-center">
                            {param.unit || "-"}
                          </td>
                          <td className="px-1 py-1.5 text-center">
                            {pulmonaryFunc ? pulmonaryFunc[param.key] : "-"}
                          </td>
                          {records.map((record) => (
                            <>
                              <td
                                key={record.testNumber + param.key + idx}
                                className="px-1 py-1.5 text-center"
                              >
                                {record.sensors[param.key]
                                  ? record.sensors[param.key].toFixed(2)
                                  : "-"}
                              </td>
                              <td className="px-1 py-1.5 text-center">
                                {record.pcPred[param.key]
                                  ? record.pcPred[param.key].toFixed(2)
                                  : "-"}
                              </td>
                            </>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 font-semibold">
                  &bull; Grafik Pengukuran VT, VC dan FC.
                </div>
                <div className="grid grid-cols-12 gap-3 mt-2">
                  <div
                    className={
                      "w-full h-64 rounded-md text-xs shadow px-2 pt-3 pb-5 col-span-12 sm:col-span-6"
                    }
                  >
                    <div className="text-center -mb-1">
                      Grafik 1. Grafik VT terhadap Waktu
                    </div>
                    {chartData.data2 !== "" && (
                      <ChartLine
                        stroke={GRAPH_COLOR}
                        data={chartData.data2.val}
                        dataKeyX="idx"
                        xAxisLabel="Waktu (s)"
                        dataKeyY={chartData.data2.key}
                        yAxisLabel="VT (L)"
                        type="number"
                        legends={legends}
                        xdomain={[0, 10]}
                        ydomain={[0, 8]}
                      />
                    )}
                  </div>
                  <div
                    className={
                      "w-full h-64 rounded-md text-xs shadow px-2 pt-3 pb-5 col-span-12 sm:col-span-6"
                    }
                  >
                    <div className="text-center -mb-1">
                      Grafik 2. F terhadap V
                    </div>
                    {chartData.data1 !== "" && (
                      <ChartLine
                        stroke={GRAPH_COLOR}
                        data={chartData.data1.val}
                        dataKeyX="VC"
                        xAxisLabel="V (L)"
                        dataKeyY={chartData.data1.key}
                        yAxisLabel="F (L/s)"
                        type="number"
                        legends={legends}
                        xdomain={[0, 16]}
                        ydomain={[0, 14]}
                      />
                    )}
                  </div>
                </div>

                <div className="mt-6 font-semibold">
                  &bull; Catatan Rekam Medis.
                </div>

                <div className="mt-1">
                  {records.map((record, idx) => (
                    <div key={"note" + idx} className="px-1">
                      <div className="mt-2">
                        Catatan rekam medis ke-{record.testNumber} :
                      </div>
                      <div>
                        {record.notes.length !== 0 ? (
                          record.notes.map((note) => (
                            <div>
                              <div>
                                <span className="mr-2">-</span>
                                {note}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p>
                            <span className="mr-2">-</span>Tidak ada catatan
                            rekam medis
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PatientReportComparison;
